import React, { useEffect, useRef } from 'react';
import { Classes, FocusStyleManager, Toaster } from '@blueprintjs/core';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import AppRoutes from './routes';
import { RootState, useTypedDispatch } from './stores';
import useThemeDetector from './components/hooks/useThemeDetector';
import { setDarkMode } from './stores/ui';
import { setUser } from './stores/user';
import { clearLocalStorage } from './utils/storage';
import { initialUser } from './utils/user';

FocusStyleManager.onlyShowFocusOnTabs();

const AppToast = Toaster.create();

const App: React.FC = () => {
  const dispatch = useTypedDispatch();
  const systemTheme = useThemeDetector();
  const { dark, toast } = useSelector((s: RootState) => s.ui);
  const navigate = useNavigate();
  const token = useSelector((s: RootState) => s.user.user.authorization.token);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Token JWT expired/invalid -> backend membalas 401 -> logout otomatis.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(undefined, (error) => {
      const auth = String(error?.config?.headers?.Authorization ?? '');
      const sentToken = auth.startsWith('Bearer ') ? auth.slice(7) : null;

      // Hanya untuk request ber-token milik sesi aktif (bukan login, bukan sisa request sesi lama).
      if (error?.response?.status === 401 && sentToken != null && sentToken === tokenRef.current) {
        tokenRef.current = null;
        dispatch(setUser(initialUser));
        clearLocalStorage();
        navigate('/login');
        AppToast.show({ intent: 'warning', message: 'Sesi Anda telah berakhir. Silakan login kembali.', timeout: 5000 });
      }

      return Promise.reject(error);
    });

    return () => axios.interceptors.response.eject(interceptor);
  }, [dispatch, navigate]);

  useEffect(() => {
    if (toast.message !== '') {
      AppToast.show({ timeout: 3000, ...toast });
    }
  }, [toast]);

  useEffect(() => {
    dispatch(setDarkMode(systemTheme));
  }, [dispatch, systemTheme]);

  return (
    <div className={dark ? Classes.DARK : ''}>
      <AppRoutes />
    </div>
  );
};


export default App;
